import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { IncepticaLogo } from "./Logos";
import { NAV_LINKS, NOVA_URL } from "../data/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-zinc-200 shadow-[0_1px_20px_-6px_rgba(24,24,27,0.15)]"
          : "bg-white/70 backdrop-blur-md border-zinc-200/70"
      }`}
    >
      <nav className={`mx-auto max-w-7xl px-6 lg:px-8 flex items-center transition-all duration-300 ${
        scrolled ? "h-16" : "h-20"
      }`}>
        <Link to="/" data-testid="nav-home-logo" className="shrink-0">
          <IncepticaLogo className="h-10" />
        </Link>

        <div className="hidden md:flex items-center gap-0.5 absolute left-1/2 -translate-x-1/2">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 rounded-full hover:bg-zinc-900/[0.04] transition-colors"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-2 ml-auto">
          <a
            href={NOVA_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-explore-nova-btn"
            className="group inline-flex items-center gap-1.5 pl-4 pr-3.5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-full hover:bg-blue-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm shadow-blue-600/20"
          >
            Explore Nova
            <ArrowUpRight className="w-4 h-4 text-white/80 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="md:hidden ml-auto p-2 text-zinc-900 rounded-lg hover:bg-zinc-100 transition-colors"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-md border-t border-zinc-200"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  className="px-3 py-3 text-base text-zinc-700 hover:text-zinc-900 border-b border-zinc-100"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-3 pt-4">
                <a
                  href={NOVA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-center px-5 py-2.5 text-sm font-medium text-white bg-zinc-900 rounded-full"
                >
                  Explore Nova
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
